import { useState } from 'react'
import { X } from 'lucide-react'

interface Props {
  value: string[] | null
  onChange: (next: string[]) => void
  placeholder?: string
}

// Controlled chip editor for a text[] column (contacts.tags / companies.tags).
// Add on Enter/comma/blur; remove via the chip ✕ or Backspace on an empty input.
// Trims + case-insensitive dedup. Token-only styling (mirrors the platform pills).
export default function TagEditor({ value, onChange, placeholder = 'Add tag…' }: Props) {
  const [input, setInput] = useState('')
  const tags = value || []

  const addTag = (raw: string) => {
    const t = raw.trim()
    setInput('')
    if (!t) return
    if (tags.some(x => x.toLowerCase() === t.toLowerCase())) return  // case-insensitive dedup
    onChange([...tags, t])
  }
  const removeTag = (t: string) => onChange(tags.filter(x => x !== t))

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {tags.map(t => (
        <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 5px 3px 9px', borderRadius: 100, fontSize: 11, fontWeight: 600, background: 'var(--hover-bg)', color: 'var(--ink-secondary)', border: '1px solid var(--border-subtle)' }}>
          {t}
          <button type="button" aria-label={`Remove ${t}`} onClick={() => removeTag(t)}
            style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
            <X style={{ width: 11, height: 11 }} />
          </button>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(input) }
          else if (e.key === 'Backspace' && !input && tags.length) removeTag(tags[tags.length - 1])
        }}
        onBlur={() => addTag(input)}
        placeholder={placeholder}
        style={{ flex: 1, minWidth: 80, padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12, color: 'var(--ink-primary)', outline: 'none' }}
        onFocus={e => { e.currentTarget.style.borderColor = 'var(--focus-ring)' }}
      />
    </div>
  )
}
